import { reportResult, paraDetail, RunDetail, applydefaultparagraphformat } from '../runs/common';
import { detectFootnote, applyStyleWithTracking } from '../runs/footnoteDetection';
import { ensureRunStyles } from '../taskpane/styleEngine';
import { getConfig } from '../taskpane/configService';
import { claimParaIds } from './structuralParaCache';

function isTableCaption(text: string): boolean {
  if (!text?.trim()) return false;
  return /^Table\s+(\d+(?:\.\d+)*|\.)/i.test(text.trim());
}

// ═══════════════════════════════════════════════════════════════════════════
// Shared scan context — bundles the mutable accumulators and config threaded
// through the scan helpers. Keeps each helper's parameter count within limits.
// ═══════════════════════════════════════════════════════════════════════════

interface ScanContext {
  counters: { footnotesFound: number; footnotesStyled: number };
  changedParas: RunDetail[];
  footnoteStyle: string;
  figureRegex: RegExp;
  allClaimedIds: string[];
}

// ═══════════════════════════════════════════════════════════════════════════
// Recursively collect all tables with their parent body.
//
// Perf: all rows loaded in one sync, all cells loaded in one sync per table
// (not per cell), so cost is O(tables × depth) instead of O(cells × depth).
// ═══════════════════════════════════════════════════════════════════════════

interface TableEntry {
  table: Word.Table;
  parentBody: Word.Body;
  isTopLevel: boolean;
}

async function collectAllTables(
  context: Word.RequestContext,
  parentBody: Word.Body,
  isTopLevel: boolean,
  result: TableEntry[]
): Promise<void> {
  const tables = parentBody.tables;
  tables.load("items");
  await context.sync();

  for (const table of tables.items) {
    table.load("rowCount");
    table.rows.load("items");
    await context.sync();

    result.push({ table, parentBody, isTopLevel });

    // Batch: issue all cell loads for every row, then sync once for the
    // whole table — one round-trip regardless of column count.
    for (const row of table.rows.items) {
      row.cells.load("items");
    }
    await context.sync();

    for (const row of table.rows.items) {
      for (const cell of row.cells.items) {
        await collectAllTables(context, cell.body, false, result);
      }
    }
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Method 1: Scan single-cell rows from the bottom up (strictly last row only)
// ═══════════════════════════════════════════════════════════════════════════

/** Returns true if this paragraph must be skipped (preserved as-is).
 *  We skip empty text, table captions, and structural styles
 *  (heading / caption table / caption figure). */
function isSkippableFootnotePara(pText: string, pStyleLower: string): boolean {
  if (!pText) return true;
  if (isTableCaption(pText)) return true;
  return (
    pStyleLower.includes("heading") ||
    pStyleLower.includes("caption table") ||
    pStyleLower.includes("caption figure")
  );
}

/** PASS 1: find the index of the FIRST paragraph in the cell that qualifies
 *  as a footnote (already-styled or pattern-detected), or -1 if none. */
async function findFirstFootnoteIndex(
  paras: Word.Paragraph[],
  context: Word.RequestContext,
  footnoteStyle: string
): Promise<number> {
  for (let pi = 0; pi < paras.length; pi++) {
    const para        = paras[pi];
    const pText       = (para.text || "").trim();
    const pStyle      = para.style || "";
    const pStyleLower = pStyle.toLowerCase();

    if (isSkippableFootnotePara(pText, pStyleLower)) continue;

    if (pStyle === footnoteStyle) {
      console.log(`    [scan] para ${pi + 1}: already "${footnoteStyle}" → footnote block starts here`);
      return pi;
    }

    const check = await detectFootnote(para, pText, context);
    if (check.detected) {
      console.log(`    [scan] para ${pi + 1}: "${pText.substring(0, 50)}" → ${check.reason} → footnote block starts here`);
      return pi;
    }
  }
  return -1;
}

/** PASS 2: from the detection point onwards, apply footnoteStyle to every
 *  eligible paragraph (skipping structural styles & captions). Returns true
 *  if at least one footnote paragraph was found in the block. */
async function applyFootnoteStyleFromIndex(
  paras: Word.Paragraph[],
  firstFootnoteIdx: number,
  context: Word.RequestContext,
  scan: ScanContext
): Promise<boolean> {
  const { counters, changedParas, footnoteStyle, allClaimedIds } = scan;
  let rowHadFootnote = false;

  for (let pi = firstFootnoteIdx; pi < paras.length; pi++) {
    const para        = paras[pi];
    const pText       = (para.text || "").trim();
    const pStyle      = para.style || "";
    const pId         = para.uniqueLocalId;
    const pStyleLower = pStyle.toLowerCase();

    if (isSkippableFootnotePara(pText, pStyleLower)) {
      if (pText) {
        console.log(`    para ${pi + 1}: "${pText.substring(0, 50)}" → skipped (structural style/caption)`);
      }
      continue;
    }

    rowHadFootnote = true;
    counters.footnotesFound++;
    allClaimedIds.push(pId);

    if (pStyle === footnoteStyle) {
      await applydefaultparagraphformat(para, footnoteStyle, context);
      console.log(`    para ${pi + 1}: "${pText.substring(0, 50)}" → (already ${footnoteStyle} — formatting reapplied)`);
    } else {
      await applyStyleWithTracking(para, footnoteStyle, context);
      counters.footnotesStyled++;
      changedParas.push(paraDetail(pId, pText, "→ " + footnoteStyle));
      await applydefaultparagraphformat(para, footnoteStyle, context);
      console.log(`    para ${pi + 1}: "${pText.substring(0, 50)}" → ✓ Styled "${footnoteStyle}" (from-detection-onwards)`);
    }
  }

  return rowHadFootnote;
}

/** Clear left/right/bottom borders on a footnote row's single cell. */
async function clearFootnoteRowBorders(
  row: Word.TableRow,
  context: Word.RequestContext
): Promise<void> {
  try {
    const cellForBorders = row.cells.items[0];
    const leftBorder     = cellForBorders.getBorder("Left");
    const rightBorder    = cellForBorders.getBorder("Right");
    const bottomBorder   = cellForBorders.getBorder("Bottom");
    leftBorder.load("type");
    rightBorder.load("type");
    bottomBorder.load("type");
    await context.sync();
    if (leftBorder.type   !== "None") leftBorder.type   = "None";
    if (rightBorder.type  !== "None") rightBorder.type  = "None";
    if (bottomBorder.type !== "None") bottomBorder.type = "None";
    await context.sync();
    console.log(`      ✓ Borders cleared (footnote row)`);
  } catch (e) {
    // Border clearing is optional cosmetic cleanup — the style change is the
    // primary action. Log and continue if a border can't be read/written.
    console.log(`      ⚠ Could not clear footnote row borders: ${e?.message ? e.message : e}`);
  }
}

async function scanBottomSingleCellRows(
  table: Word.Table,
  context: Word.RequestContext,
  scan: ScanContext
): Promise<void> {
  if (table.rowCount < 2) return;

  console.log(`  Scanning bottom-up: looking for single-cell rows`);

  // Only the last row is ever treated as a footnote row. row.cells was already
  // loaded for every row inside collectAllTables — no re-load/sync needed here.
  const r         = table.rowCount - 1;
  const row       = table.rows.items[r];
  const cellCount = row.cells.items.length;

  if (cellCount !== 1) {
    console.log(`  Row ${r + 1} has ${cellCount} cells — no single-cell footnote row at table bottom`);
    return;
  }

  console.log(`  Row ${r + 1}: single-cell row, scanning paragraphs`);

  {
    const cell      = row.cells.items[0];
    const cellParas = cell.body.paragraphs;
    cellParas.load("items");
    await context.sync();

    // LOAD PHASE — batch all property loads, then one sync.
    for (const para of cellParas.items) {
      para.load("text, style, uniqueLocalId");
    }
    await context.sync();
    // READ PHASE — all three properties guaranteed populated below.

    // ═════════════════════════════════════════════════════════════════════
    // Two-pass approach (Python-compatible "from detection point onwards"):
    //   Pass 1: walk paragraphs top-down, find the index of the FIRST
    //           eligible paragraph that qualifies as a footnote.
    //   Pass 2: starting from that index, convert every eligible paragraph
    //           to footnoteStyle (skipping structural styles & table
    //           captions). Paragraphs BEFORE the detection point are left
    //           untouched.
    //
    // Rationale: a footnote block in a cell may have multi-line
    // continuations or abbreviations that don't individually match the
    // strict pattern set. Everything from the first detection onwards
    // belongs to that footnote block; anything earlier (e.g. trailing
    // body text from the data section) must be preserved.
    // ═════════════════════════════════════════════════════════════════════
    const stylesBefore = scan.counters.footnotesStyled;
    const firstFootnoteIdx = await findFirstFootnoteIndex(cellParas.items, context, scan.footnoteStyle);

    let rowHadFootnote = false;
    if (firstFootnoteIdx === -1) {
      console.log(`  Row ${r + 1}: no footnote detected in cell — leaving as-is`);
    } else {
      rowHadFootnote = await applyFootnoteStyleFromIndex(cellParas.items, firstFootnoteIdx, context, scan);
    }
    const stylesApplied = scan.counters.footnotesStyled - stylesBefore;

    if (rowHadFootnote) {
      await clearFootnoteRowBorders(row, context);
    } else {
      console.log(`  Row ${r + 1}: no footnotes detected — borders left unchanged`);
    }

    console.log(`  ✓ bottom single-cell row scanned, ${stylesApplied} styled`);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Find the index of the first paragraph in parentBody that comes strictly
// AFTER the table's last row.
//
// Perf: all compareLocationWith calls are issued as proxies first, then
// resolved in a single sync — O(1) round-trips instead of O(n).
// ═══════════════════════════════════════════════════════════════════════════

async function findAfterTableIndex(
  table: Word.Table,
  bodyParas: Word.ParagraphCollection,
  context: Word.RequestContext
): Promise<number> {
  const bodyParaCount = bodyParas.items.length;
  if (bodyParaCount === 0) return -1;

  try {
    // Re-load last row cells — self-contained, not relying on prior state.
    const lastRow = table.rows.items[table.rowCount - 1];
    lastRow.cells.load("items");
    await context.sync();

    const lastCell      = lastRow.cells.items[lastRow.cells.items.length - 1];
    const lastCellRange = lastCell.body.getRange(Word.RangeLocation.whole);

    // Build all comparison proxies AND load paragraph text in one pass,
    // then resolve everything in a single sync.
    const comparisons = bodyParas.items.map(para => {
      para.load("text");
      return lastCellRange.compareLocationWith(para.getRange(Word.RangeLocation.whole));
    });
    await context.sync(); // resolves text loads and all comparisons together

    for (let bi = 0; bi < bodyParaCount; bi++) {
      const rel = comparisons[bi].value as string;
      if (rel === "Before" || rel === "AdjacentBefore") {
        console.log(`  First paragraph after table: index ${bi} ("${(bodyParas.items[bi].text || "").substring(0, 40)}")`);
        return bi;
      }
    }
  } catch (e) {
    console.log(`  findAfterTableIndex failed: ${e.message}`);
  }

  return -1;
}

// ═══════════════════════════════════════════════════════════════════════════
// Method 2: Scan paragraphs AFTER the table in its parent body
// ═══════════════════════════════════════════════════════════════════════════

/** Hard-stop conditions for the below-table scan: a new heading, a table or
 *  figure caption — anything signalling the footnote block has ended. */
function belowTableStop(pText: string, pStyle: string, figureRegex: RegExp): boolean {
  const lowStyle = pStyle.toLowerCase();
  if (lowStyle.includes("heading")) return true;
  if (lowStyle.includes("caption table")) return true;
  if (lowStyle.includes("caption figure")) return true;
  if (isTableCaption(pText)) return true;
  return figureRegex.test(pText.trim());
}

/** Process one candidate paragraph below the table.
 *  Returns 'stop' when the scan should halt, 'empty' for a blank paragraph
 *  (caller tracks the consecutive-empty run), or 'continue' otherwise. */
async function processBelowTablePara(
  para: Word.Paragraph,
  context: Word.RequestContext,
  scan: ScanContext,
  index: number,
  afterTableIdx: number
): Promise<'stop' | 'empty' | 'continue'> {
  const { counters, changedParas, footnoteStyle, figureRegex, allClaimedIds } = scan;
  const pText  = para.text  || "";
  const pStyle = para.style || "";
  const pId    = para.uniqueLocalId;

  if (belowTableStop(pText, pStyle, figureRegex)) return 'stop';

  if (!pText.trim()) return 'empty';

  if (pStyle === footnoteStyle) {
    await applydefaultparagraphformat(para, footnoteStyle, context);
    allClaimedIds.push(pId);
    return 'continue';
  }

  const check = await detectFootnote(para, pText, context);
  console.log(`  [${index - afterTableIdx + 1}] "${pText.trim().substring(0, 50)}" → ${check.detected ? check.reason : "not footnote"}`);

  if (!check.detected) return 'stop';

  counters.footnotesFound++;
  allClaimedIds.push(pId);

  // pStyle !== footnoteStyle is guaranteed here (handled above), so always style.
  para.style = footnoteStyle;
  await context.sync();
  await applydefaultparagraphformat(para, footnoteStyle, context);
  counters.footnotesStyled++;
  changedParas.push(paraDetail(pId, pText.trim(), "→ " + footnoteStyle));
  console.log(`    ✓ Styled "${footnoteStyle}"`);
  return 'continue';
}

async function scanParagraphsBelowTable(
  table: Word.Table,
  parentBody: Word.Body,
  context: Word.RequestContext,
  scan: ScanContext
): Promise<void> {
  console.log(`  Scanning paragraphs below table (top-level)...`);

  const bodyParas = parentBody.paragraphs;
  bodyParas.load("items");
  await context.sync();

  const bodyParaCount = bodyParas.items.length;
  if (bodyParaCount === 0) return;

  // Reuse the paragraphs already loaded above — findAfterTableIndex no longer
  // issues its own body load/sync.
  const afterTableIdx = await findAfterTableIndex(table, bodyParas, context);

  if (afterTableIdx === -1) {
    console.log(`  No paragraphs after table in parent body`);
    return;
  }

  const LOOK_AHEAD = 15;
  const scanEnd    = Math.min(afterTableIdx + LOOK_AHEAD, bodyParaCount);

  // LOAD PHASE — batch all candidate paragraph properties, then one sync.
  for (let idx = afterTableIdx; idx < scanEnd; idx++) {
    bodyParas.items[idx].load("text, style, uniqueLocalId");
  }
  await context.sync();
  // READ PHASE — all properties guaranteed populated below.

  const stylesBefore = scan.counters.footnotesStyled;
  let consecutiveEmpty = 0;

  for (let idx = afterTableIdx; idx < scanEnd; idx++) {
    const outcome = await processBelowTablePara(bodyParas.items[idx], context, scan, idx, afterTableIdx);

    if (outcome === 'stop') break;
    if (outcome === 'empty') {
      consecutiveEmpty++;
      if (consecutiveEmpty >= 2) break;
      continue;
    }
    consecutiveEmpty = 0;
  }

  const footnoteCount = scan.counters.footnotesStyled - stylesBefore;
  if (footnoteCount > 0) {
    console.log(`  ✓ ${footnoteCount} footnote(s)\n`);
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Main export
// ═══════════════════════════════════════════════════════════════════════════

export async function run12() {
  await Word.run(async (context) => {
    try {
      const cfg           = getConfig();
      const footnoteStyle = cfg.run12_tableFootnotes.footnoteStyle;
      const figureRegex   = new RegExp(cfg.run8_figures.figureRegex, "i");

      await ensureRunStyles(context, 12);
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;
      await context.sync();

      const scan: ScanContext = {
        counters: { footnotesFound: 0, footnotesStyled: 0 },
        changedParas: [],
        footnoteStyle,
        figureRegex,
        allClaimedIds: [],
      };

      console.log("=".repeat(70));
      console.log(`=== TABLE FOOTNOTE TEXT CHECKER (${cfg.clientName}) ===`);
      console.log("=".repeat(70));

      const allTableEntries: TableEntry[] = [];
      await collectAllTables(context, context.document.body, true, allTableEntries);

      const totalTables = allTableEntries.length;
      console.log(`Total tables (including nested): ${totalTables}\n`);

      if (totalTables === 0) {
        claimParaIds([], 'tableFootnote');
        reportResult('Table Footnote — no tables found', [
          { label: 'No tables in document', note: 'Nothing to process' }
        ]);
        return;
      }

      for (let t = 0; t < allTableEntries.length; t++) {
        const { table, parentBody, isTopLevel } = allTableEntries[t];

        console.log(`${"─".repeat(50)}`);
        console.log(`[Table ${t + 1} of ${totalTables}] ${table.rowCount} rows | ${isTopLevel ? 'TOP-LEVEL' : 'NESTED'}`);

        await scanBottomSingleCellRows(table, context, scan);

        if (isTopLevel) {
          await scanParagraphsBelowTable(table, parentBody, context, scan);
        }
      }

      claimParaIds(scan.allClaimedIds, 'tableFootnote');

      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();

      console.log("=".repeat(70));
      console.log(`Tables: ${totalTables} | Found: ${scan.counters.footnotesFound} | Styled: ${scan.counters.footnotesStyled}`);
      console.log(`Cache: registered ${scan.allClaimedIds.length} table-footnote para ID(s)`);
      console.log("✓ Table Footnote check complete!");

      if (scan.counters.footnotesStyled > 0) {
        reportResult('Table Footnote Styling Complete', [
          { label: 'Footnotes found below tables',                   count: scan.counters.footnotesFound  },
          { label: `Footnotes styled with ${footnoteStyle} style`,   count: scan.counters.footnotesStyled },
          ...scan.changedParas,
        ]);
      } else {
        reportResult('Table Footnote — no changes needed', [
          { label: 'No footnote text found below tables', note: 'No changes made' },
        ]);
      }
    } catch (error) {
      console.error("Error in run12:", error);
      console.error("Message:", error.message);
    }
  });
}